import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	useAppendix7HasDraftQuery,
	useContinueAppendix7DraftMutation,
	useCreateAppendix7Mutation,
	useEditAppendix7Mutation,
	useGetAppendix7DraftQuery,
} from '@/api/forms-management/appendix7';
import EditReferralForICCMWForm from './components/EditReferralForICCMWForm';
import EditAppendix7Form from './components/EditAppendix7Form';
import EditAppendix7ToForm from './components/EditAppendix7ToForm';
import { FormManagementStatus } from '@/api/enum/common.enum';
import EditServiceTerminationForm from './components/EditServiceTerminationForm';

export default function CreateAppendix7Page() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [createAppendix7] = useCreateAppendix7Mutation();
	const { preAdmissionId, formType, admissionRequestId } = useParams();
	const showModal = useModal();
	const [editAppendix7] = useEditAppendix7Mutation();

	const { data: details }: any = useGetAppendix7DraftQuery(
		{ id: preAdmissionId || admissionRequestId || '', type: formType || '' },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const appendix7Ref = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const appendix7ToRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referralForICCMWRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const serviceTerminationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async (type: string) => {
		const results = await Promise.all([
			appendix7Ref.current?.validateForm() || Promise.resolve(false),
			appendix7ToRef.current?.validateForm() || Promise.resolve(false),
			referralForICCMWRef.current?.validateForm() || Promise.resolve(false),
			serviceTerminationRef.current?.validateForm() || Promise.resolve(false),
		]);

		console.log(results);

		const validate = results.some((result) => result === false);
		if (type == 'verify') {
			enqueueSnackbar(
				validate
					? t('snackbar.fieldsVerifiedFalse')
					: t('snackbar.fieldsVerifiedTrue'),
				{
					variant: validate ? 'error' : 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			return;
		}
		if (validate) return;

		const { branchId, userId, fromOfficerInCharge, fromRefNo, fromDate } = {
			...(await appendix7Ref.current?.getFormData()),
		};
		const {
			toOfficerInCharge,
			nameOfOrganization,
			toRefNo,
			toDated,
			toFaxNo,
			toTotalPages,
		} = {
			...(await appendix7ToRef.current?.getFormData()),
		};
		const {
			nameEn,
			hkicNo,
			sex,
			age,
			ageEstimated,
			referralDate,

			counsellingToServiceUser,
			counsellingToFamily,
			successfulEngagement,
			referralToCps,
			receivingTherapy,
			occupationalTherapy,
			outreaching,
			trainingServices,
			peerSupportService,
			clinicalPsychologicalService,
			others,
			othersSpecify,
		} = {
			...(await referralForICCMWRef.current?.getFormData()),
		};
		const {
			dateOfTermination,

			interventionCompleted,
			admittedToResidential,
			admittedToPsychiatricHospital,
			persistentlyUnmotivated,
			selfWithdrawal,
			untraceable,
			serviceTerminationOthers,
			serviceTerminationOthersSpecify,

			serviceTerminationRemarks,

			contactWorker,
			contactTel,
			contactOfficerInCharge,
		} = {
			...(await serviceTerminationRef.current?.getFormData()),
		};

		const formData = {
			branchId,
			userId,
			fromOfficerInCharge,
			fromRefNo,
			fromDate,
			toOfficerInCharge,
			nameOfOrganization,
			toRefNo,
			toDated,
			toFaxNo,
			toTotalPages,
			nameEn,
			hkicNo,
			sex,
			age,
			ageEstimated: !!ageEstimated,
			referralDate,

			counsellingToServiceUser,
			counsellingToFamily,
			successfulEngagement,
			referralToCps,
			receivingTherapy,
			occupationalTherapy,
			outreaching,
			trainingServices,
			peerSupportService,
			clinicalPsychologicalService,
			others,
			othersSpecify,
			dateOfTermination,

			interventionCompleted,
			admittedToResidential,
			admittedToPsychiatricHospital,
			persistentlyUnmotivated,
			selfWithdrawal,
			untraceable,
			serviceTerminationOthers,
			serviceTerminationOthersSpecify,

			serviceTerminationRemarks,

			contactWorker,
			contactTel,
			contactOfficerInCharge,

			id: details.id || '',
			formStatus:
				type == 'draft'
					? FormManagementStatus.draft
					: FormManagementStatus.submitted,
		};

		if (!formData.id) delete formData.id;

		if (type == 'submit') {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.completeFormTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.completeFormText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonText={t('button.saveDraft')}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						saveApi({
							...formData,
							formStatus: FormManagementStatus.submitted,
						});
					}}
					onOtherConfirm={async () => {
						saveApi({ ...formData, formStatus: FormManagementStatus.draft });
					}}
				/>
			));
		} else if (type == 'draft') {
			saveApi({ ...formData, formStatus: FormManagementStatus.draft });
		}
	};

	const saveApi = async (formData: any) => {
		const response = details.id
			? await editAppendix7(formData)
			: await createAppendix7(formData);
		if ('data' in response) {
			enqueueSnackbar(
				formData.formStatus == FormManagementStatus.submitted
					? t('snackbar.faceSheetSubmitSuccess')
					: t('snackbar.draftFaceSheetSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate(
				preAdmissionId
					? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
					: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
			);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ marginTop: 3 }}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h4' marginBottom={'38px'}>
							{t('drawerListItems.newAppendix7')}
						</Typography>
						<EditAppendix7Form
							ref={appendix7Ref}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditAppendix7ToForm
							ref={appendix7ToRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditReferralForICCMWForm
							ref={referralForICCMWRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditServiceTerminationForm
							ref={serviceTerminationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
					</CardContent>

					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button
							type='submit'
							onClick={() =>
								navigate(
									preAdmissionId
										? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
										: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
								)
							}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('draft')}
							endIcon={<EditNoteOutlinedIcon />}
						>
							{t('button.saveDraft')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('verify')}
							endIcon={<TaskOutlinedIcon />}
						>
							{t('button.verifyFields')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('submit')}
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</Box>
		</>
	);
}
